// Adapter: thin wrappers around borogove that produce values typed per ./types.ts.
//
// This file is the ONLY place in src/lib/xmpp/** that bridges the borogove
// runtime to our declared types. We do NOT use `as any`; instead we declare
// "Borogove*Like" interfaces below that describe the runtime shape we can rely
// on. This keeps the boundary explicit and reviewable.
//
// All exported functions are pure: they invoke the wrapped borogove method
// once per call, never cache, never store. Re-call on every snapshot if you
// want a re-read.
//
// Tracks REQ-CN-001 in traceability-matrix.md.

import type {
	Chat,
	ChatUiState,
	Client,
	ClientListeners,
	Listener,
	Message,
	PresenceInfo,
	PresenceState,
	Reaction,
	Reactions,
	ReplyContext,
} from "./types.js";
import { AdapterError } from "./types.js";

// NOTE: `createClient` and `startRegistration` live in ./factories.ts.
// They require `import * as borogove` at value level, which cannot be
// evaluated under tsx in Node (borogove is Haxe-compiled for browser).
// Splitting them out keeps this file (and adapter.test.ts) Node-loadable.

// ---------------------------------------------------------------------------
// Borogove runtime shapes (the parts we depend on)
// ---------------------------------------------------------------------------

interface BorogoveReactionLike {
	readonly senderId?: unknown;
	readonly timestamp?: unknown;
	readonly key?: unknown;
	readonly text?: unknown;
}

interface BorogoveReplyLike {
	readonly id?: unknown;
	readonly serverId?: unknown;
	readonly localId?: unknown;
	readonly body?: unknown;
	readonly text?: unknown;
}

interface BorogoveReactionsLike {
	entries?: () => Iterable<[unknown, unknown]>;
}

interface BorogoveMessageLike {
	readonly localId?: unknown;
	readonly serverId?: unknown;
	readonly chatId?: unknown; // may be string | (() => string)
	readonly from?: unknown;
	readonly senderId?: unknown;
	readonly text?: unknown;
	readonly timestamp?: unknown;
	readonly retracted?: unknown;
	readonly correctedId?: unknown;
	readonly replyTo?: unknown;
	readonly replyToMessage?: unknown;
	readonly reactions?: unknown;
}

interface BorogoveParticipantLike {
	readonly presence?: unknown;
	readonly status?: unknown;
	readonly lastSeen?: unknown;
}

interface BorogoveChatLike {
	readonly chatId?: unknown;
	readonly isBookmarked?: unknown;
	readonly uiState?: unknown;
	readonly lastMessage?: unknown;
	getDisplayName?: () => string | null;
	isTrusted?: () => boolean;
	isApp?: () => boolean;
	unreadCount?: () => number;
	setBookmarked?: (v: boolean) => void;
	markReadUpTo?: (msgOrId: unknown) => void;
	getParticipantDetails?: (jid: string) => BorogoveParticipantLike | null | undefined;
}

interface BorogoveClientLike {
	getChats?: () => ReadonlyArray<unknown>;
	start?: () => void;
	usePassword?: (password: string) => void;
	subscribePush?: (
		reg: ServiceWorkerRegistration,
		pushUrl: string,
		keyPair: CryptoKeyPair,
		expirationDays: number,
	) => Promise<unknown> | unknown;
	addChatMessageListener?: (cb: (m: unknown) => void) => unknown;
	addChatsUpdatedListener?: (cb: (cs: ReadonlyArray<unknown>) => void) => unknown;
	addStatusOnlineListener?: (cb: () => void) => unknown;
	addStatusOfflineListener?: (cb: () => void) => unknown;
	addConnectionFailedListener?: (cb: () => void) => unknown;
	addPasswordNeededListener?: (cb: () => void) => unknown;
	removeEventListener?: (token: unknown) => void;
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/**
 * Parse an ISO 8601 timestamp from borogove into Unix-ms.
 * Returns 0 for invalid input rather than throwing — borogove may produce
 * empty/null timestamps for in-flight messages.
 */
function parseTimestamp(raw: unknown): number {
	if (typeof raw === "number" && Number.isFinite(raw)) return raw;
	if (typeof raw === "string" && raw.length > 0) {
		const n = Date.parse(raw);
		return Number.isFinite(n) ? n : 0;
	}
	return 0;
}

/**
 * Resolve a `chatId`-style field that may be either a property or a zero-arg
 * method on the borogove object. Returns undefined if neither yields a string.
 */
function readChatIdField(obj: unknown): string | undefined {
	if (obj === null || obj === undefined) return undefined;
	const v = (obj as { chatId?: unknown }).chatId;
	if (typeof v === "string") return v.length > 0 ? v : undefined;
	if (typeof v === "function") {
		try {
			const result = (v as () => unknown).call(obj);
			return typeof result === "string" && result.length > 0 ? result : undefined;
		} catch {
			return undefined;
		}
	}
	return undefined;
}

function presenceState(raw: unknown): PresenceState {
	if (typeof raw !== "string") return "unknown";
	const s = raw.toLowerCase();
	if (s === "online" || s === "available" || s === "chat") return "online";
	if (s === "away" || s === "xa") return "away";
	if (s === "dnd" || s === "busy") return "dnd";
	if (s === "offline" || s === "unavailable") return "offline";
	return "unknown";
}

function chatUiState(raw: unknown): ChatUiState {
	if (typeof raw !== "string" && typeof raw !== "number") return "unknown";
	const s = String(raw).toLowerCase();
	if (s === "active" || s === "0") return "active";
	if (s === "invited" || s === "1") return "invited";
	if (s === "archived" || s === "2") return "archived";
	return "unknown";
}

function nonEmptyString(v: unknown): string | undefined {
	return typeof v === "string" && v.length > 0 ? v : undefined;
}

// ---------------------------------------------------------------------------
// adaptMessage
// ---------------------------------------------------------------------------

function adaptReplyTo(raw: unknown): ReplyContext | undefined {
	if (raw === null || raw === undefined) return undefined;
	const r = raw as BorogoveReplyLike;
	const id = nonEmptyString(r.id) ?? nonEmptyString(r.serverId) ?? nonEmptyString(r.localId);
	if (id === undefined) return undefined;
	const body = typeof r.body === "string"
		? r.body
		: (typeof r.text === "string" ? r.text : "");
	return { id, body };
}

function adaptReactionEntry(raw: unknown, fallbackKey: string): Reaction | undefined {
	if (raw === null || raw === undefined) return undefined;
	const r = raw as BorogoveReactionLike;
	const senderId = nonEmptyString(r.senderId);
	if (senderId === undefined) return undefined;
	return {
		senderId,
		timestamp: parseTimestamp(r.timestamp),
		key: typeof r.key === "string" ? r.key : fallbackKey,
		text: typeof r.text === "string" ? r.text : fallbackKey,
	};
}

function adaptReactions(raw: unknown): Reactions | undefined {
	if (raw === null || raw === undefined) return undefined;
	const reactionsObj = raw as BorogoveReactionsLike;
	const entriesFn = reactionsObj.entries;
	if (typeof entriesFn !== "function") return undefined;

	const out: Array<readonly [string, ReadonlyArray<Reaction>]> = [];
	let total = 0;
	for (const [emojiRaw, sendersRaw] of entriesFn.call(reactionsObj)) {
		if (typeof emojiRaw !== "string" || emojiRaw.length === 0) continue;
		const list: Reaction[] = [];
		if (sendersRaw instanceof Set) {
			// Legacy Map<emoji, Set<senderId>> shape from older borogove builds.
			for (const sender of sendersRaw) {
				if (typeof sender === "string" && sender.length > 0) {
					list.push({ senderId: sender, timestamp: 0, key: emojiRaw, text: emojiRaw });
				}
			}
		} else if (Array.isArray(sendersRaw)) {
			for (const item of sendersRaw) {
				const r = adaptReactionEntry(item, emojiRaw);
				if (r !== undefined) list.push(r);
			}
		}
		if (list.length > 0) {
			out.push([emojiRaw, list] as const);
			total += 1;
		}
	}
	if (total === 0) return undefined;
	return { size: total, entries: out };
}

/**
 * Wrap a borogove ChatMessage as a typed Message.
 *
 * @throws AdapterError if input is null, undefined, or has no resolvable chatId.
 */
export function adaptMessage(raw: unknown): Message {
	if (raw === null || raw === undefined) {
		throw new AdapterError("adaptMessage received null or undefined", raw);
	}
	const m = raw as BorogoveMessageLike;
	const chatId = readChatIdField(m);
	if (chatId === undefined) {
		throw new AdapterError("adaptMessage: message has no resolvable chatId", raw);
	}

	const fromField = nonEmptyString(m.from) ?? nonEmptyString(m.senderId) ?? "";
	const replyToSource = m.replyTo ?? m.replyToMessage;

	return {
		localId: nonEmptyString(m.localId),
		serverId: nonEmptyString(m.serverId),
		chatId,
		from: fromField,
		text: typeof m.text === "string" ? m.text : "",
		timestamp: parseTimestamp(m.timestamp),
		retracted: m.retracted === true,
		correctedId: nonEmptyString(m.correctedId),
		replyTo: adaptReplyTo(replyToSource),
		reactions: adaptReactions(m.reactions),
	};
}

// ---------------------------------------------------------------------------
// adaptChat
// ---------------------------------------------------------------------------

/**
 * Wrap a borogove Chat as a typed Chat.
 *
 * @throws AdapterError if input is null, undefined, or missing chatId.
 */
export function adaptChat(raw: unknown): Chat {
	if (raw === null || raw === undefined) {
		throw new AdapterError("adaptChat received null or undefined", raw);
	}
	const c = raw as BorogoveChatLike;
	const chatId = typeof c.chatId === "string" ? c.chatId : "";
	if (chatId.length === 0) {
		throw new AdapterError("adaptChat: chat has no chatId", raw);
	}

	const displayNameRaw = typeof c.getDisplayName === "function"
		? c.getDisplayName()
		: null;
	const displayName = (typeof displayNameRaw === "string" && displayNameRaw.length > 0)
		? displayNameRaw
		: chatId;

	let lastMessage: Message | undefined;
	if (c.lastMessage !== null && c.lastMessage !== undefined) {
		try {
			lastMessage = adaptMessage(c.lastMessage);
		} catch {
			// A chat is still useful without its last-message preview; swallow.
			lastMessage = undefined;
		}
	}

	const isTrusted = typeof c.isTrusted === "function" ? c.isTrusted() === true : false;
	const isApp = typeof c.isApp === "function" ? c.isApp() === true : false;
	const unreadCount = typeof c.unreadCount === "function" ? (c.unreadCount() ?? 0) : 0;
	const setBookmarkedFn = c.setBookmarked;
	const markReadUpToFn = c.markReadUpTo;
	const getParticipantDetailsFn = c.getParticipantDetails;

	return {
		chatId,
		displayName,
		isBookmarked: c.isBookmarked === true,
		isTrusted,
		isApp,
		uiState: chatUiState(c.uiState),
		lastMessage,
		unreadCount,
		setBookmarked: (value: boolean) => {
			setBookmarkedFn?.(value);
		},
		markReadUpTo: (messageId: string) => {
			// Borogove's declared signature takes a ChatMessage object, but the
			// runtime tolerates a string ID. Pass through; REQ-CN-011 (WASM-backed
			// adapter) must match this contract.
			markReadUpToFn?.(messageId);
		},
		getParticipantPresence: (jid: string): PresenceInfo | undefined => {
			if (typeof getParticipantDetailsFn !== "function") return undefined;
			const details = getParticipantDetailsFn(jid);
			if (details === null || details === undefined) return undefined;
			const statusMessage = typeof details.status === "string" && details.status.length > 0
				? details.status
				: undefined;
			const lastSeen = typeof details.lastSeen === "number" && Number.isFinite(details.lastSeen)
				? details.lastSeen
				: undefined;
			return {
				state: presenceState(details.presence),
				statusMessage,
				lastSeen,
			};
		},
	};
}

// ---------------------------------------------------------------------------
// adaptClient
// ---------------------------------------------------------------------------

function makeUnsubscribe(client: BorogoveClientLike, token: unknown): Listener {
	let active = true;
	return {
		unsubscribe: () => {
			if (!active) return;
			active = false;
			client.removeEventListener?.(token);
		},
	};
}

function inertListener(): Listener {
	return { unsubscribe: () => {} };
}

/**
 * Wrap a borogove Client as a typed Client.
 *
 * @param raw the borogove.Client instance
 * @param jid the bare JID used to construct the client (borogove doesn't expose this directly)
 * @throws AdapterError if raw is null/undefined or jid is empty.
 */
export function adaptClient(raw: unknown, jid: string): Client {
	if (raw === null || raw === undefined) {
		throw new AdapterError("adaptClient received null or undefined", raw);
	}
	if (typeof jid !== "string" || jid.length === 0) {
		throw new AdapterError("adaptClient: jid must be a non-empty string", jid);
	}
	const c = raw as BorogoveClientLike;

	const listeners: ClientListeners = {
		onChatMessage: (cb) => {
			const reg = c.addChatMessageListener;
			if (typeof reg !== "function") return inertListener();
			const token = reg.call(c, (m: unknown) => {
				try {
					cb(adaptMessage(m));
				} catch {
					// Swallow per-event adapter failures so one bad message doesn't
					// kill the listener. Real telemetry is a future requirement.
				}
			});
			return makeUnsubscribe(c, token);
		},
		onChatsUpdated: (cb) => {
			const reg = c.addChatsUpdatedListener;
			if (typeof reg !== "function") return inertListener();
			const token = reg.call(c, (cs: ReadonlyArray<unknown>) => {
				const adapted: Chat[] = [];
				for (const item of cs) {
					try {
						adapted.push(adaptChat(item));
					} catch {
						// skip unadaptable
					}
				}
				cb(adapted);
			});
			return makeUnsubscribe(c, token);
		},
		onStatusOnline: (cb) => {
			const reg = c.addStatusOnlineListener;
			if (typeof reg !== "function") return inertListener();
			return makeUnsubscribe(c, reg.call(c, cb));
		},
		onStatusOffline: (cb) => {
			const reg = c.addStatusOfflineListener;
			if (typeof reg !== "function") return inertListener();
			return makeUnsubscribe(c, reg.call(c, cb));
		},
		onConnectionFailed: (cb) => {
			const reg = c.addConnectionFailedListener;
			if (typeof reg !== "function") return inertListener();
			return makeUnsubscribe(c, reg.call(c, cb));
		},
		onPasswordNeeded: (cb) => {
			const reg = c.addPasswordNeededListener;
			if (typeof reg !== "function") return inertListener();
			return makeUnsubscribe(c, reg.call(c, cb));
		},
	};

	return {
		jid,
		listeners,
		getChats: () => {
			if (typeof c.getChats !== "function") return [];
			const out: Chat[] = [];
			for (const rawChat of c.getChats()) {
				try {
					out.push(adaptChat(rawChat));
				} catch {
					// skip unadaptable
				}
			}
			return out;
		},
		start: () => {
			c.start?.();
		},
		usePassword: (password: string) => {
			c.usePassword?.(password);
		},
		subscribePush: async (reg, pushUrl, keyPair, expirationDays) => {
			const fn = c.subscribePush;
			if (typeof fn !== "function") return;
			await fn(reg, pushUrl, keyPair, expirationDays);
		},
	};
}
